// src/components/Agency.vue
<template>
  <div class="agency-page">
    <div class="page-header">
      <h1>Agencies</h1>
      <Button label="Add Agency" icon="pi pi-plus" @click="openAddModal" />
    </div>

    <DataTable
      :value="agencies"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      :loading="loading"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      :globalFilterFields="['name', 'type']"
      v-model:filters="filters"
      filterDisplay="menu"
    >
      <template #header>
        <div class="table-header">
          <h3>Agencies ({{ agencies.length }})</h3>
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Search agencies..." />
          </IconField>
        </div>
      </template>
      <Column field="type" header="Type" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search type" />
        </template>
      </Column>
      <Column field="name" header="Name" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <span class="agency-name">{{ data.name }}</span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
        </template>
      </Column>
      <Column header="Actions" :exportable="false">
        <template #body="{ data }">
          <div class="actions">
            <Button icon="pi pi-pencil" severity="info" size="small" @click="openEditModal(data)" />
            <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteAgency(data.id)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="showModal"
      :header="isEditing ? 'Edit Agency' : 'Add Agency'"
      :modal="true"
      :closable="false"
      :dismissableMask="false"
      :style="{ width: '500px' }"
    >
      <form @submit.prevent="saveAgency" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <Select
                id="type"
                v-model="form.type"
                :options="typeOptions"
                optionLabel="label"
                optionValue="value"
                :class="{ 'p-invalid': !form.type && formSubmitted }"
              />
              <label for="type">Type *</label>
            </IftaLabel>
          </div>
          <div class="form-group">
            <IftaLabel>
              <InputText
                id="name"
                v-model="form.name"
                :class="{ 'p-invalid': !form.name && formSubmitted }"
                required
              />
              <label for="name">Name *</label>
            </IftaLabel>
          </div>
        </div>
        <div class="modal-actions">
          <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="closeModal" />
          <Button type="submit" :label="isEditing ? 'Update Agency' : 'Create Agency'" icon="pi pi-check" :loading="saving" />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import IftaLabel from 'primevue/iftalabel'
import Swal from 'sweetalert2'
import api from '../axios.js'

const agencies = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const loading = ref(false)
const formSubmitted = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  name: { operator: 'and', constraints: [{ value: null, matchMode: 'startsWith' }] },
  type: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const form = ref({ id: null, type: '', name: '' })

const typeOptions = [
  { label: 'National Government Agency', value: 'NGA' },
  { label: 'DOF Attached Agency', value: 'DAA' },
  { label: 'OP', value: 'OP' },
  { label: 'Others', value: 'OTHERS' },
]

const loadAgencies = async () => {
  try {
    loading.value = true
    const response = await api.get('/agencies')
    agencies.value = response.data
  } catch (error) {
    console.error('Error loading agencies:', error)
    agencies.value = []
  } finally {
    loading.value = false
  }
}

const openAddModal = () => {
  isEditing.value = false
  form.value = { id: null, type: '', name: '' }
  showModal.value = true
}

const openEditModal = (agency) => {
  isEditing.value = true
  form.value = { ...agency }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formSubmitted.value = false
  saving.value = false
  form.value = { id: null, type: '', name: '' }
}

const saveAgency = async () => {
  formSubmitted.value = true
  if (!form.value.name || !form.value.type) return

  const result = await Swal.fire({
    title: 'Confirm Action',
    text: 'Confirming',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    target: document.body
  })
  if (!result.isConfirmed) return

  try {
    saving.value = true
    const data = { name: form.value.name, type: form.value.type }

    if (isEditing.value) {
      await api.put(`/agencies/${form.value.id}`, data)
    } else {
      await api.post('/agencies', data)
    }

    closeModal()
    loadAgencies()

    Swal.fire({
      title: 'Success!',
      text: `Agency ${isEditing.value ? 'updated' : 'created'} successfully`,
      icon: 'success',
      confirmButtonText: 'OK',
      target: document.body
    })
  } catch (error) {
    console.error('Error saving agency:', error)
  } finally {
    saving.value = false
  }
}

const deleteAgency = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Agency',
    text: 'Are you sure you want to delete this agency?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })

  if (result.isConfirmed) {
    try {
      await api.delete(`/agencies/${id}`)
      loadAgencies()
      Swal.fire({
        title: 'Deleted!',
        text: 'Agency has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting agency:', error)
    }
  }
}

onMounted(() => loadAgencies())
</script>

<style scoped>
.agency-page {
  padding: 2rem;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.agency-name {
  font-weight: 600;
  color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-select {
  width: 100% !important;
  box-sizing: border-box;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .agency-page {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}
</style>
